import { Injectable } from '@angular/core';
import { AuthState, User } from '@fitscovery/auth/data-access';
import { Partner, PartnerState } from '@fitscovery/partner/data-access';
import { Guid } from '@fitscovery/common/types';
import { Action, NgxsOnInit, Select, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { ApplyForMembership, GetMembershipsByMemberAndPartnerId, OpenMembershipDetailsModal, RefreshMembership, ApplyForMembershipSubscription, HasAccessToWorkoutOrProgram } from '../actions';
import { ApiMembershipService } from '../api-membership.service';
import { MembershipStatus } from '../enums';
import { Membership } from '../models';
import { produce } from 'immer';
import { tap } from 'rxjs/operators';

/**
 * The membership state model used in `StateContext<T>`
 *
 * @export
 * @interface MembershipStateModel
 */
export interface MembershipStateModel {
	isProcessing: boolean;
	isMembershipButtonClicked: boolean;
	hasAppliedForFreeMembershipWithNoResponse: boolean;
  hasAccessToWorkoutOrProgram: boolean;
	loaded: boolean;
	memberships: Membership[] | null;
	gettingMemberships: boolean;
}

export const membershipStateDefaults: MembershipStateModel = {
	isProcessing: false,
	isMembershipButtonClicked: false,
	hasAppliedForFreeMembershipWithNoResponse: false,
  hasAccessToWorkoutOrProgram: false,
	loaded: false,
	memberships: null,
	gettingMemberships: false,
};

/**
 * The class responsible for handling the user auth slice state
 *
 * @export
 * @class MembershipState
 * @implements {NgxsOnInit}
 */
@State<MembershipStateModel>({
	name: 'membership',
	defaults: membershipStateDefaults
})

@Injectable()
export class MembershipState implements NgxsOnInit {

	@Selector()
	static memberships(state: MembershipStateModel): Membership[] {
		return state.memberships!;
	}

  @Selector()
	static activeMemberships(state: MembershipStateModel): Membership[] {
		return state.memberships!.filter(e => e.status === MembershipStatus.Active);
	}

  @Selector()
	static hasAccessToWorkoutOrProgram(state: MembershipStateModel): boolean {
		return state.hasAccessToWorkoutOrProgram;
	}

  // For Refactor

  @Select(AuthState.user) userAuthState$: Observable<User>;
	@Select(PartnerState.partner) partnerState$: Observable<Partner>;
	memberId: Guid;
	partnerId: Guid;

	@Selector()
	static isProcessing(state: MembershipStateModel): boolean {
		return state.isProcessing;
	}

	@Selector()
	static hasActiveStatus(state: MembershipStateModel): boolean {
		return state.memberships!.some(f => f.status === MembershipStatus.Active);
	}

	@Selector()
	static hasEventsAccess(state: MembershipStateModel): boolean {
		return state.memberships!.some(f => f.forEvents === true);
	}

	@Selector()
	static hasBookingAccess(state: MembershipStateModel): boolean {
		return state.memberships!.some(f => f.forDirectBookings === true);
	}

	@Selector()
	static hasUnlimitedVideosAccess(state: MembershipStateModel): boolean {
		return state.memberships!.some(f => f.forDirectBookings === true);
	}

		// FOR REFACTORING: This only checks the Guid.empty free subscription but not the PMS that are free.
	@Selector()
	static hasActiveFreeSubscriptionStatus(state: MembershipStateModel): boolean {
		const activeMembership = state.memberships!.filter(f => f.status === MembershipStatus.Active);

		if (activeMembership.length > 0 && activeMembership.length === 1) {
			return activeMembership[0].partnerMembershipSubscriptionId.toString() === Guid.empty;
		}

		return false;
	}

	@Selector()
	static hasPendingStatus(state: MembershipStateModel): boolean {
		return state.memberships!.some(f => f.status === MembershipStatus.Pending);
	}

	@Selector()
	static hasRemainingTicket(state: MembershipStateModel): boolean {
		return state.memberships!.some(f => f.status === MembershipStatus.Active && (f.availableTicket > 0 || f.availableTicket === -1));
	}

	@Selector()
	static noOfRemainingTickets(state: MembershipStateModel): number {
		const isUnlimited = state.memberships!.some(f => f.status === MembershipStatus.Active && f.availableTicket === -1);
		if (isUnlimited) {
			return Number.MAX_VALUE;
		}
		else {
			let remainingTickets: number = 0;
			state.memberships!.filter(f => f.status === MembershipStatus.Active && f.availableTicket > 0).forEach((membership: Membership) => {
				remainingTickets += membership.availableTicket;
			});
			return remainingTickets
		}
	}

	@Selector()
	static isMembershipButtonClicked(state: MembershipStateModel): boolean {
		return state.isMembershipButtonClicked;
	}

	@Selector()
	static hasAppliedForFreeMembershipWithNoResponse(state: MembershipStateModel): boolean {
		return state.hasAppliedForFreeMembershipWithNoResponse;
	}

	@Selector()
	static loaded(state: MembershipStateModel): boolean {
		return state.loaded;
	}

	@Selector()
	static gettingMemberships(state: MembershipStateModel): boolean {
		return state.gettingMemberships;
	}

	constructor(
    private store: Store,
    private membershipService: ApiMembershipService
  ) { }

	ngxsOnInit(ctx: StateContext<MembershipStateModel>): void {
	}

	@Action(ApplyForMembership)
	async applyForMembership(ctx: StateContext<MembershipStateModel>, action: ApplyForMembership) {
		try {
			this.isProcessing(ctx, true, false);
			const state: MembershipStateModel = ctx.getState();
			const membership: any = await this.membershipService.applyForMembership(action.memberInfo).toPromise();
      
			if (membership) {
        const value = produce(state, draft => { draft.memberships!.push(membership) })
        ctx.setState(value);
			} else {
				ctx.patchState({
					isProcessing: false,
					isMembershipButtonClicked: false,
					hasAppliedForFreeMembershipWithNoResponse: true,
					loaded: true //to catch if membership response is null
				});
			}
		} catch (exception: any) {
			// TODO: Implement error handling
			let errors = '';
			if (exception?.error?.errors) {
				errors = exception?.error?.errors.join('\n ');
			}

			throw new Error(`Failed to apply for membership. ` + errors);
		}
	}

	@Action(ApplyForMembershipSubscription)
	async applyForMembershipSubscription(ctx: StateContext<MembershipStateModel>, action: ApplyForMembershipSubscription) {
		try {
			this.isProcessing(ctx, true, false);
			const state: MembershipStateModel = ctx.getState();
			const membership: any = await this.membershipService.applyForMembershipSubscription(action.memberInfo).toPromise();

			if (membership) {
				ctx.patchState({
					memberships: [membership, ...state.memberships!],
					isProcessing: false,
					isMembershipButtonClicked: false,
					hasAppliedForFreeMembershipWithNoResponse: false,
					loaded: true
				});
			} else {
				ctx.patchState({
					isProcessing: false,
					isMembershipButtonClicked: false,
					hasAppliedForFreeMembershipWithNoResponse: true,
					loaded: true //to catch if membership response is null
				});
			}
		} catch (exception: any) {
			// TODO: Implement error handling
			let errors = '';
			if (exception.error.errors) {
				errors = exception.error.errors.join('\n ');
			}

			throw new Error(`Failed to apply for membership. ` + errors);
		}
	}

	@Action(GetMembershipsByMemberAndPartnerId)
	async getMembershipsByMemberAndPartnerId(ctx: StateContext<MembershipStateModel>, action: GetMembershipsByMemberAndPartnerId) {
		try {
			this.memberId = action.memberId;
			this.partnerId = action.partnerId;
			this.isProcessing(ctx, true, false);
			this.getMemberships(ctx, action.memberId, action.partnerId);

		} catch (error) {
			console.info(error);
			throw new Error(`Failed to get membership data.`);
		}
	}

	@Action(OpenMembershipDetailsModal)
	async openMembershipDetailsModal(ctx: StateContext<MembershipStateModel>) {
		ctx.patchState({
			isMembershipButtonClicked: true
		});
	}

	@Action(HasAccessToWorkoutOrProgram)
	hasAccessToWorkoutOrProgram(ctx: StateContext<MembershipStateModel>, action: HasAccessToWorkoutOrProgram) {
    const partnerId = this.store.selectSnapshot(e => e.partner.data.id) as Guid;
    const userId = this.store.selectSnapshot(e => e.user.info.id) as Guid;
    return (this.membershipService.hasAccessToWorkoutOrProgram(partnerId, userId, action.selectedId) as any).pipe(
      tap((hasAccess: boolean) => ctx.patchState({ hasAccessToWorkoutOrProgram: hasAccess }))
    );
	}

	@Action(RefreshMembership)
	async refreshMembership(ctx: StateContext<MembershipStateModel>) {
		if (this.memberId === null && this.partnerId === null) {
			return;
		}

		this.isProcessing(ctx, true, false);

		this.getMemberships(ctx, this.memberId, this.partnerId);
	}

	private async getMemberships(ctx: StateContext<MembershipStateModel>, memberId: Guid, partnerId: Guid) {
		ctx.patchState({ gettingMemberships: true });
		const memberships = await this.membershipService.getMembershipsByMemberAndPartnerId(memberId, partnerId).toPromise();
		ctx.patchState({ gettingMemberships: false });

		if (memberships !== undefined) {
			ctx.patchState({

				memberships: [...memberships],
				isProcessing: false,
				loaded: true
			});
		} else {
			ctx.patchState({
				memberships: null,
				isProcessing: false,
				isMembershipButtonClicked: false,
				loaded: true //to catch if membership response is null
			});
		}
	}

	private isProcessing(ctx: StateContext<MembershipStateModel>, value: boolean, loaded: boolean) {
		ctx.patchState({
			isProcessing: value,
			loaded: loaded
		});
	}

}
